import { FunctionComponent, JSX, RefObject, useLayoutEffect, useRef, useState } from 'react'
import styles from './ButtonTextIcon.module.scss'
import {
    Add,
    ArrowBack,
    ArrowForward,
    Check,
    Close,
    Delete,
    Edit,
    Error,
    Print,
    RemoveRedEye,
    SaveAlt,
    Settings,
    Sync
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import Badge from '@mui/material/Badge'

interface ButtonTextIconProps {
    type:
        | 'add'
        | 'return'
        | 'confirm'
        | 'delete'
        | 'edit'
        | 'cancel'
        | 'close'
        | 'export'
        | 'settings'
        | 'continue'
        | 'update'
        | 'custom'
        | 'reset'
        | 'next'
        | 'view'
        | 'print'
        | 'create'
    onClick?: () => void
    parent?: boolean
    fullWidth?: boolean
    nowrap?: boolean
    className?: string
    disabled?: boolean
    customText?: string
    customIcon?: JSX.Element
    badge?: number
}

const ButtonTextIcon: FunctionComponent<ButtonTextIconProps> = ({
    type,
    onClick,
    parent,
    fullWidth,
    nowrap = false,
    className,
    disabled,
    customText,
    customIcon,
    badge
}: ButtonTextIconProps) => {
    const { t } = useTranslation()

    switch (type) {
        case 'add':
        case 'create':
            return (
                <ButtonTextIconTemplate
                    icon={<Add />}
                    text={type === 'add' ? t('add') : t('create')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'return':
            return (
                <ButtonTextIconTemplate
                    icon={<ArrowBack />}
                    text={t('return')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'delete':
            return (
                <ButtonTextIconTemplate
                    icon={<Delete />}
                    text={t('delete')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'confirm':
            return (
                <ButtonTextIconTemplate
                    icon={<Check />}
                    text={t('confirm')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'edit':
            return (
                <ButtonTextIconTemplate
                    icon={<Edit />}
                    text={t('edit')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'cancel':
        case 'close':
            return (
                <ButtonTextIconTemplate
                    icon={<Close />}
                    text={type === 'cancel' ? t('cancel') : t('close')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'export':
            return (
                <ButtonTextIconTemplate
                    icon={<SaveAlt />}
                    text={t('export')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'settings':
            return (
                <ButtonTextIconTemplate
                    icon={<Settings />}
                    text={t('settings')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'update':
        case 'reset':
            return (
                <ButtonTextIconTemplate
                    icon={<Sync />}
                    text={type === 'update' ? t('update') : t('reset')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'next':
        case 'continue':
            return (
                <ButtonTextIconTemplate
                    icon={<ArrowForward />}
                    text={type === 'next' ? t('next') : t('continue')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'view':
            return (
                <ButtonTextIconTemplate
                    icon={<RemoveRedEye />}
                    text={t('view')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'print':
            return (
                <ButtonTextIconTemplate
                    icon={<Print />}
                    text={t('print')}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        case 'custom':
            return badge ? (
                <Badge badgeContent={badge} color={'primary'}>
                    <ButtonTextIconTemplate
                        icon={customIcon ? customIcon : <Error />}
                        text={customText ? customText : ''}
                        onClick={onClick}
                        parent={parent}
                        fullWidth={fullWidth}
                        nowrap={nowrap}
                        className={className}
                        disabled={disabled}
                    />
                </Badge>
            ) : (
                <ButtonTextIconTemplate
                    icon={customIcon ? customIcon : <Error />}
                    text={customText ? customText : ''}
                    onClick={onClick}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
        default:
            return (
                <ButtonTextIconTemplate
                    icon={<Error />}
                    text='Error'
                    onClick={() => {}}
                    parent={parent}
                    fullWidth={fullWidth}
                    nowrap={nowrap}
                    className={className}
                    disabled={disabled}
                />
            )
    }
}

export default ButtonTextIcon

interface ButtonTextIconTemplateProps {
    icon: JSX.Element
    text: string
    onClick?: () => void
    parent?: boolean
    fullWidth?: boolean
    nowrap?: boolean
    className?: string
    disabled?: boolean
    badge?: number
}

const ButtonTextIconTemplate: FunctionComponent<ButtonTextIconTemplateProps> = ({
    icon,
    text,
    onClick,
    parent,
    fullWidth,
    nowrap = false,
    className,
    disabled
}) => {
    const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null)
    const contentRef: RefObject<HTMLSpanElement | null> = useRef<HTMLSpanElement>(null)
    const labelRef: RefObject<HTMLSpanElement | null> = useRef<HTMLSpanElement>(null)
    const [compact, setCompact] = useState<boolean>(false)

    useLayoutEffect(() => {
        const button: HTMLButtonElement | null = buttonRef.current
        const content: HTMLSpanElement | null = contentRef.current
        const label: HTMLSpanElement | null = labelRef.current
        if (!nowrap || !button || !content || !label) {
            setCompact(false)
            return
        }

        function measure(): void {
            if (!button || !content || !label) return
            const buttonStyle: CSSStyleDeclaration = getComputedStyle(button)
            const contentStyle: CSSStyleDeclaration = getComputedStyle(content)
            const availableWidth: number =
                button.clientWidth -
                parseFloat(buttonStyle.paddingLeft || '0') -
                parseFloat(buttonStyle.paddingRight || '0')
            const gap: number = parseFloat(contentStyle.columnGap) || 0
            const direction: number = contentStyle.direction === 'rtl' ? -1 : 1
            button.style.setProperty('--compact-offset', `${(direction * (label.offsetWidth + gap)) / 2}px`)
            setCompact(content.offsetWidth > availableWidth + 1)
        }

        measure()
        const animationFrameId: number = requestAnimationFrame(measure)
        if (typeof ResizeObserver === 'undefined') return
        const observer: ResizeObserver = new ResizeObserver(measure)
        observer.observe(button)
        observer.observe(content)
        observer.observe(label)
        if (button.parentElement) observer.observe(button.parentElement)
        return () => {
            cancelAnimationFrame(animationFrameId)
            observer.disconnect()
        }
    }, [text, nowrap])

    return (
        <button
            ref={buttonRef}
            data-nowrap={nowrap || undefined}
            data-compact={(nowrap && compact) || undefined}
            aria-label={text || undefined}
            title={nowrap && compact ? text : undefined}
            className={`${styles.button} 
						${className}
						${disabled ? styles.buttonDisabled : ''}
						${
                            parent
                                ? disabled
                                    ? styles.buttonParentDisabled
                                    : styles.buttonParent
                                : disabled
                                  ? styles.buttonDaycareDisabled
                                  : styles.buttonDaycare
                        }
						${fullWidth ? styles.buttonFullWidth : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            <span ref={contentRef} className={styles.content} aria-hidden={text ? true : undefined}>
                <span className={styles.icon}>{icon}</span>
                {text !== '' && (
                    <span ref={labelRef} className={styles.label}>
                        {text}
                    </span>
                )}
            </span>
        </button>
    )
}
